import { useState } from "react";
import {
  WaveBlockKind,
  waveBlockKindLabel,
  type WaveBlock,
} from "@vida/wave-core";
import {
  blockKindColor,
  blockKindGlyph,
} from "./waveFeedTokens";

type Props = {
  blocks: WaveBlock[];
  onRestore: (blockId: string) => void;
  onDeleteForever: (blockId: string) => void;
  onClose: () => void;
};

function blockPreviewLabel(block: WaveBlock): string {
  const p = block.payload;
  switch (block.kind) {
    case WaveBlockKind.Text:
      return (p.text ?? "").trim() || "Testo";
    case WaveBlockKind.Heading:
      return (p.text ?? "").trim() || "Titolo";
    case WaveBlockKind.Decision:
    case WaveBlockKind.Poll:
    case WaveBlockKind.Signoff:
    case WaveBlockKind.Task:
    case WaveBlockKind.TodoList:
    case WaveBlockKind.Gallery:
      return (p.title ?? "").trim() || waveBlockKindLabel(block.kind);
    case WaveBlockKind.File:
      return p.fileName ?? "Allegato";
    case WaveBlockKind.TidePageRef:
    case WaveBlockKind.DriftTodoRef:
    case WaveBlockKind.ReefArticleRef:
    case WaveBlockKind.PulseDocRef:
      return p.refTitle ?? waveBlockKindLabel(block.kind);
    default:
      return waveBlockKindLabel(block.kind);
  }
}

function formatArchivedAt(iso?: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });
}

export function WaveArchivedWidgetsPanel({
  blocks,
  onRestore,
  onDeleteForever,
  onClose,
}: Props) {
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  return (
    <div
      className="wave-archived-panel-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="wave-archived-panel"
        role="dialog"
        aria-labelledby="wave-archived-panel-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wave-archived-panel-header">
          <h2 id="wave-archived-panel-title">Widget archiviati</h2>
          <button type="button" className="link-btn" onClick={onClose}>
            Chiudi
          </button>
        </header>

        {blocks.length === 0 ? (
          <p className="wave-archived-panel-empty">Nessun widget archiviato in questo thread.</p>
        ) : (
          <ul className="wave-archived-panel-list">
            {blocks.map((block) => {
              const color = blockKindColor(block.kind);
              const preview = blockPreviewLabel(block);
              const archivedLabel = formatArchivedAt(block.payload.archivedAt);
              const confirming = confirmDeleteId === block.id;

              return (
                <li key={block.id} className="wave-archived-panel-item">
                  <div className="wave-archived-panel-item-main">
                    <span
                      className="wave-archived-panel-kind"
                      style={{
                        color,
                        background: `color-mix(in srgb, ${color} 15%, transparent)`,
                      }}
                      aria-hidden
                    >
                      {blockKindGlyph(block.kind)}
                    </span>
                    <div className="wave-archived-panel-item-text">
                      <strong>{preview}</strong>
                      <span className="wave-archived-panel-meta">
                        {waveBlockKindLabel(block.kind)}
                        {archivedLabel ? ` · Archiviato ${archivedLabel}` : ""}
                      </span>
                    </div>
                  </div>
                  <div className="wave-archived-panel-actions">
                    {confirming ? (
                      <>
                        <span className="wave-archived-panel-confirm-label">Eliminare?</span>
                        <button
                          type="button"
                          className="wave-archived-panel-btn danger"
                          onClick={() => {
                            onDeleteForever(block.id);
                            setConfirmDeleteId(null);
                          }}
                        >
                          Sì, elimina
                        </button>
                        <button
                          type="button"
                          className="wave-archived-panel-btn"
                          onClick={() => setConfirmDeleteId(null)}
                        >
                          Annulla
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="wave-archived-panel-btn primary"
                          onClick={() => onRestore(block.id)}
                        >
                          Ripristina
                        </button>
                        <button
                          type="button"
                          className="wave-archived-panel-btn danger-outline"
                          onClick={() => setConfirmDeleteId(block.id)}
                        >
                          Elimina
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
